<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/helper/toast';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

// A exclusão dispara um soft-delete + cleanup em background (Inboxes::CleanupJob).
// O request volta rápido; o toast.promise dá o feedback loading → sucesso/erro
// num balão só (em vez do snackbar antigo que sumia sem rastro).
const deleteInbox = ({ id }) => {
  toast.promise(store.dispatch('inboxes/delete', id), {
    loading: t('INBOX_MGMT.DELETE.API.LOADING'),
    success: t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'),
    error: t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'),
  });
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-inbox"
        accent="cyan"
        :title="$t('INBOX_MGMT.HEADER')"
        :subtitle="$t('INBOX_MGMT.DESCRIPTION')"
      />
    </template>
    <woot-loading-state v-if="uiFlags.isFetching" />
    <p
      v-else-if="!inboxesList.length"
      class="flex flex-col items-center justify-center h-full text-base text-n-slate-12 p-8"
    >
      {{ $t('INBOX_MGMT.LIST.404') }}
    </p>
    <table v-else class="min-w-full overflow-x-auto">
      <tbody class="divide-y divide-n-weak flex-1 text-n-slate-12">
        <tr v-for="inbox in inboxesList" :key="inbox.id">
          <td class="py-4 ltr:pr-4 rtl:pl-4">
            <div class="flex items-center flex-row gap-4">
              <div
                v-if="inbox.avatar_url"
                class="bg-n-alpha-3 rounded-full size-12 p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
              >
                <Avatar
                  :src="inbox.avatar_url"
                  :name="inbox.name"
                  :size="30"
                  rounded-full
                />
              </div>
              <div
                v-else
                class="size-12 flex justify-center items-center bg-n-alpha-3 rounded-full p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
              >
                <ChannelIcon class="size-5" :inbox="inbox" />
              </div>
              <div>
                <span class="block font-medium capitalize">
                  {{ inbox.name }}
                </span>
                <ChannelName
                  :channel-type="inbox.channel_type"
                  :medium="inbox.medium"
                />
              </div>
            </div>
          </td>

          <td class="py-4">
            <div class="flex gap-1 justify-end">
              <router-link
                :to="{
                  name: 'settings_inbox_show',
                  params: { inboxId: inbox.id },
                }"
              >
                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                  icon="i-lucide-settings"
                  slate
                  xs
                  faded
                />
              </router-link>
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                @click="openDelete(inbox)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </PageLayout>
</template>
